import React, { useState, useEffect } from 'react';
import { useFetch } from '../api';
import { executeAction } from './SandboxApi';

const CONDITIONS = ['good', 'damaged', 'defective', 'worn'];
const DISPOSITIONS = ['return_to_stock', 'return_to_supplier', 'scrapped', 'inspect'];

const formatLabel = (str) => str
  .split('_')
  .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
  .join(' ');

function ReceiveReturnModal({
  isOpen, onClose, returnData, connectionId, onSuccess, onError,
}) {
  const trackstar = useFetch();
  const [lineItems, setLineItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen || !returnData) return;
    const items = (returnData.line_items || []).map((li) => {
      const expected = li.expected_quantity || li.quantity || 1;
      const alreadyReceived = (li.received_quantity || 0) >= expected;
      const remaining = expected - (li.received_quantity || 0);
      return {
        sku: li.sku,
        expected_quantity: expected,
        remaining,
        received_quantity: alreadyReceived ? 0 : remaining,
        condition: 'good',
        disposition: 'return_to_stock',
        included: !alreadyReceived,
        alreadyReceived,
      };
    });
    setLineItems(items);
  }, [isOpen, returnData]);

  if (!isOpen || !returnData) return null;

  const handleFieldChange = (index, field, value) => {
    const updated = [...lineItems];
    updated[index] = { ...updated[index], [field]: value };
    setLineItems(updated);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);

    const included = lineItems.filter((li) => li.included);
    if (included.length === 0) {
      onError('Select at least one line item to receive.');
      setLoading(false);
      return;
    }

    const body = {
      line_items: included.map((li) => ({
        sku: li.sku,
        received_quantity: li.received_quantity,
        condition: li.condition,
        disposition: li.disposition,
      })),
    };

    executeAction(trackstar, connectionId, 'PUT', `sandbox/simulate/returns/${returnData.id}/receive`, body)
      .then(() => {
        setLoading(false);
        onSuccess();
        onClose();
      })
      .catch((err) => {
        setLoading(false);
        onError(String(err));
      });
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50">
      <div className="modal-backdrop" onClick={onClose} aria-hidden="true" />
      <form className="modal max-w-2xl max-h-[85vh]" onSubmit={handleSubmit}>
        <div className="modal-header">
          <h2 className="text-lg font-semibold text-surface-900">Receive Return</h2>
          <p className="text-xs text-surface-500 mt-0.5">
            Inspect each line item and set condition & disposition
          </p>
        </div>

        <div className="modal-body space-y-4">
          {lineItems.map((li, index) => (
            <div
              key={li.sku}
              className={`border rounded-lg p-4 space-y-3 transition-colors ${
                li.included ? 'border-surface-200 bg-white' : 'border-surface-100 bg-surface-50 opacity-60'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={li.included}
                      disabled={li.alreadyReceived}
                      onChange={(e) => handleFieldChange(index, 'included', e.target.checked)}
                      className="rounded border-surface-300"
                    />
                    <span className="text-sm font-medium text-surface-800 font-mono">{li.sku}</span>
                  </label>
                  {li.alreadyReceived && (
                    <span className="text-[10px] text-green-600 bg-green-50 px-1.5 py-0.5 rounded font-medium">
                      Received
                    </span>
                  )}
                </div>
                <span className="text-xs text-surface-400">
                  Expected:
                  {' '}
                  {li.expected_quantity}
                </span>
              </div>

              {li.included && (
                <div className="grid grid-cols-3 gap-3 pl-7">
                  <div>
                    <div className="text-xs text-surface-500 mb-1">Received Qty</div>
                    <input
                      type="number"
                      min={0}
                      max={li.remaining}
                      value={li.received_quantity}
                      onChange={(e) => handleFieldChange(index, 'received_quantity', Number(e.target.value))}
                      className="input text-sm w-full"
                    />
                  </div>
                  <div>
                    <div className="text-xs text-surface-500 mb-1">Condition</div>
                    <select
                      value={li.condition}
                      onChange={(e) => handleFieldChange(index, 'condition', e.target.value)}
                      className="input text-sm w-full"
                    >
                      {CONDITIONS.map((c) => (
                        <option key={c} value={c}>{formatLabel(c)}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <div className="text-xs text-surface-500 mb-1">Disposition</div>
                    <select
                      value={li.disposition}
                      onChange={(e) => handleFieldChange(index, 'disposition', e.target.value)}
                      className="input text-sm w-full"
                    >
                      {DISPOSITIONS.map((d) => (
                        <option key={d} value={d}>{formatLabel(d)}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="modal-footer">
          <button type="button" onClick={onClose} className="btn btn-secondary">Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={loading || lineItems.every((li) => !li.included)}>
            {loading ? 'Receiving...' : 'Receive Return'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default ReceiveReturnModal;
